import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, SxProps, Theme, Typography } from '@mui/material'
import { Button, Icon } from '@lawpilots/design-system'

import type { MenusNavItem } from '@/common/types'
import { loadViews } from '../../../hooks'
import CustomPalette from '../../../theme/CustomPalette'

type ToolBarProps = {
  sx?: SxProps<Theme>
  withCustomButton?: boolean
  buttonOnly?: boolean
  title?: string
  description?: string
  actionLabel?: string
  customButtonLabel?: string
  altCustomButtonLabel?: string
  menus?: MenusNavItem[]
  openAction?: () => void
  closeAction?: () => void
  /** Any other prop is handed to every menu view. */
  [viewProp: string]: unknown
}

export const ToolBar = ({
  withCustomButton = false,
  buttonOnly = false,
  title,
  description,
  actionLabel,
  customButtonLabel,
  altCustomButtonLabel,
  menus,
  openAction,
  closeAction,
  sx,
  ...viewProps
}: ToolBarProps): JSX.Element => {
  const { i18n } = useTranslation('general')
  const [Menus, setMenus] = useState<Array<JSX.Element>>([])

  useEffect(() => {
    loadViews({
      views: menus,
      action: closeAction,
      ...viewProps,
    }).then(setMenus)
  }, [menus, i18n.language])

  return (
    <Box
      key={i18n.language}
      sx={[
        {
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          m: -1,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      {!buttonOnly && (
        <Box sx={{ m: 1 }}>
          <Typography
            sx={{
              color: CustomPalette.NightBlue200,
              fontSize: '24px',
              ml: '3px',
            }}
            variant="h3">
            {title}
          </Typography>
          <Typography sx={{ m: 1 }} variant="h5">
            {description}
          </Typography>
        </Box>
      )}
      {actionLabel && (
        <Box sx={{ m: 1 }}>
          <Button data-testid="toolbar-action-button" onClick={openAction}>
            {actionLabel}
          </Button>
        </Box>
      )}
      {withCustomButton && (
        <Button icon={<Icon name="plus" />} onClick={openAction}>
          {customButtonLabel ?? altCustomButtonLabel}
        </Button>
      )}
      <Suspense fallback={null}>{Menus}</Suspense>
    </Box>
  )
}
